<template>
  <component :is="embedded ? 'div' : 'Teleport'" :to="embedded ? undefined : 'body'">
    <div
      v-if="embedded || modelValue"
      :class="embedded ? 'epc-embedded' : 'epc-backdrop'"
      @click.self="embedded ? undefined : close()"
    >
      <div
        :class="embedded ? 'epc-embedded-inner' : 'epc-modal'"
        role="dialog"
        :aria-modal="!embedded"
      >
        <div v-if="!embedded" class="epc-header">
          <div>
            <h5 class="epc-title">Pay components &amp; adjustments</h5>
            <p class="epc-subtitle">
              {{ employeeLabel }} · {{ groupLabel }}
            </p>
          </div>
          <button type="button" class="epc-close" aria-label="Close" @click="close">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="epc-tabs" :class="{ 'epc-tabs-embedded': embedded }">
          <button
            type="button"
            class="epc-tab"
            :class="{ active: activeTab === 'recurring' }"
            @click="activeTab = 'recurring'"
          >
            <i class="bx bx-repeat"></i>
            Recurring
            <span v-if="employeeComponents.length" class="epc-tab-count">{{ employeeComponents.length }}</span>
          </button>
          <button
            type="button"
            class="epc-tab"
            :class="{ active: activeTab === 'adjustments' }"
            @click="activeTab = 'adjustments'"
          >
            <i class="bx bx-calendar-edit"></i>
            Adjustments
            <span v-if="pendingAdjustments.length" class="epc-tab-count warn">{{ pendingAdjustments.length }}</span>
          </button>
        </div>

        <div class="epc-body" :class="{ 'epc-body-embedded': embedded }">
          <p class="epc-hint">
            <i class="bx bx-info-circle"></i>
            Recurring items apply every payroll run in Payroll Hub. Period adjustments are one-off bonuses or deductions for the selected effective month.
          </p>

          <div v-if="loading" class="epc-loading">
            <div class="epc-spinner"></div>
            <span>Loading pay components…</span>
          </div>

          <!-- Recurring pay components -->
          <div v-else-if="activeTab === 'recurring'">
            <form class="epc-form-card" @submit.prevent="saveComponent">
              <div class="epc-form-card-title"><i class="bx bx-plus-circle"></i> Add recurring component</div>
              <div class="epc-form-grid">
                <div class="epc-field">
                  <label>Component</label>
                  <select v-model="componentForm.pay_component_id" class="epc-input" required>
                    <option value="">Select…</option>
                    <optgroup label="Earnings (allowances)">
                      <option
                        v-for="c in earningComponents"
                        :key="'e-' + c.id"
                        :value="String(c.id)"
                      >
                        {{ c.component_name }}
                      </option>
                    </optgroup>
                    <optgroup label="Deductions">
                      <option
                        v-for="c in deductionComponents"
                        :key="'d-' + c.id"
                        :value="String(c.id)"
                      >
                        {{ c.component_name }}
                      </option>
                    </optgroup>
                  </select>
                </div>
                <div class="epc-field">
                  <label>Amount (KES)</label>
                  <input
                    v-model.number="componentForm.amount"
                    type="number"
                    min="0"
                    step="0.01"
                    class="epc-input"
                    required
                  />
                </div>
                <div class="epc-field">
                  <label>Effective from</label>
                  <input v-model="componentForm.effective_from" type="date" class="epc-input" />
                </div>
                <div class="epc-field epc-field-action">
                  <label>&nbsp;</label>
                  <button type="submit" class="epc-btn primary" :disabled="saving">
                    {{ saving ? 'Saving…' : 'Add / update' }}
                  </button>
                </div>
              </div>
            </form>

            <div v-if="!employeeComponents.length" class="epc-empty">
              <i class="bx bx-layer"></i>
              <p>No recurring components yet</p>
            </div>
            <div v-else class="epc-item-list">
              <div v-for="row in employeeComponents" :key="row.id" class="epc-item-card">
                <div class="epc-item-main">
                  <span class="epc-pill" :class="row.component_type === 'Deduction' ? 'ded' : 'earn'">
                    {{ row.component_type === 'Deduction' ? 'Deduction' : 'Earning' }}
                  </span>
                  <div class="epc-item-info">
                    <strong>{{ row.component_name }}</strong>
                    <span class="epc-item-meta">{{ row.is_taxable == 1 ? 'Taxable' : 'Non-taxable' }}</span>
                  </div>
                </div>
                <div class="epc-item-right">
                  <span class="epc-item-amount" :class="row.component_type === 'Deduction' ? 'ded' : 'earn'">
                    {{ row.component_type === 'Deduction' ? '−' : '+' }}{{ formatMoney(row.amount) }}
                  </span>
                  <button type="button" class="epc-icon-btn danger" title="Remove" @click="removeComponent(row)">
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Period adjustments -->
          <div v-else>
            <form class="epc-form-card" @submit.prevent="saveAdjustment">
              <div class="epc-form-card-title"><i class="bx bx-plus-circle"></i> Add period adjustment</div>
              <div class="epc-form-grid">
                <div class="epc-field">
                  <label>Type</label>
                  <select v-model="adjustmentForm.adjustment_type" class="epc-input" required>
                    <option value="Bonus">Bonus (earning)</option>
                    <option value="Allowance">Allowance (earning)</option>
                    <option value="Commission">Commission (earning)</option>
                    <option value="Penalty">Penalty (deduction)</option>
                    <option value="Advance">Advance (deduction)</option>
                    <option value="Other">Other (earning)</option>
                  </select>
                </div>
                <div class="epc-field">
                  <label>Description</label>
                  <input v-model="adjustmentForm.description" type="text" class="epc-input" required placeholder="e.g. March performance bonus" />
                </div>
                <div class="epc-field">
                  <label>Amount (KES)</label>
                  <input
                    v-model.number="adjustmentForm.amount"
                    type="number"
                    min="0"
                    step="0.01"
                    class="epc-input"
                    :disabled="adjustmentForm.is_percentage"
                    required
                  />
                </div>
                <div class="epc-field">
                  <label>Effective date</label>
                  <input v-model="adjustmentForm.effective_date" type="date" class="epc-input" required />
                </div>
                <div class="epc-field epc-check">
                  <label>
                    <input v-model="adjustmentForm.is_taxable" type="checkbox" />
                    Taxable
                  </label>
                </div>
                <div class="epc-field epc-field-action">
                  <label>&nbsp;</label>
                  <button type="submit" class="epc-btn primary" :disabled="saving">
                    {{ saving ? 'Saving…' : 'Add adjustment' }}
                  </button>
                </div>
              </div>
            </form>

            <div v-if="!pendingAdjustments.length" class="epc-empty">
              <i class="bx bx-calendar"></i>
              <p>No pending adjustments</p>
            </div>
            <div v-else class="epc-item-list">
              <div v-for="adj in pendingAdjustments" :key="adj.id" class="epc-item-card">
                <div class="epc-item-main">
                  <span class="epc-pill" :class="isDeductionType(adj.adjustment_type) ? 'ded' : 'earn'">
                    {{ adj.adjustment_type }}
                  </span>
                  <div class="epc-item-info">
                    <strong>{{ adj.description }}</strong>
                    <span class="epc-item-meta">Effective {{ formatDate(adj.effective_date) }}</span>
                  </div>
                </div>
                <div class="epc-item-right">
                  <span class="epc-item-amount" :class="isDeductionType(adj.adjustment_type) ? 'ded' : 'earn'">
                    {{ isDeductionType(adj.adjustment_type) ? '−' : '+' }}{{ formatMoney(adj.amount) }}
                  </span>
                  <button type="button" class="epc-icon-btn danger" title="Cancel" @click="cancelAdjustment(adj)">
                    <i class="bx bx-x"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="message" class="epc-message" :class="{ error: messageError }">{{ message }}</div>
        </div>

        <div v-if="!embedded" class="epc-footer">
          <button type="button" class="epc-btn outline" @click="close">Close</button>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import { authUrl, execute } from '@/api';

export default {
  name: 'EmployeePayComponentsPanel',
  props: {
    modelValue: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
    employee: { type: Object, default: null },
    payrollConfigId: { type: [String, Number], default: '' },
    payrollConfigs: { type: Array, default: () => [] }
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      activeTab: 'recurring',
      loading: false,
      saving: false,
      message: '',
      messageError: false,
      catalog: [],
      employeeComponents: [],
      pendingAdjustments: [],
      componentForm: {
        pay_component_id: '',
        amount: '',
        effective_from: today
      },
      adjustmentForm: {
        adjustment_type: 'Bonus',
        description: '',
        amount: '',
        effective_date: today,
        is_percentage: false,
        is_taxable: true
      }
    };
  },
  computed: {
    employeeLabel() {
      if (!this.employee) return 'Employee';
      return [this.employee.firstName, this.employee.lastName].filter(Boolean).join(' ') || 'Employee';
    },
    groupLabel() {
      const id = Number(this.payrollConfigId);
      const g = this.payrollConfigs.find((c) => Number(c.id) === id);
      return g?.config_name || (id ? `Group #${id}` : 'No payroll group');
    },
    earningComponents() {
      return this.catalog.filter((c) => c.component_type === 'Earning');
    },
    deductionComponents() {
      return this.catalog.filter((c) => c.component_type === 'Deduction');
    }
  },
  watch: {
    modelValue(open) {
      if (open && !this.embedded) {
        this.message = '';
        this.loadAll();
      }
    },
    employee: {
      handler(emp) {
        if (this.embedded && emp?.id) {
          this.message = '';
          this.loadAll();
        }
      },
      immediate: true
    },
    payrollConfigId() {
      if (this.embedded && this.employee?.id) {
        this.loadAll();
      }
    }
  },
  methods: {
    isDeductionType(type) {
      return ['Penalty', 'Advance'].includes(type);
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    async post(fn, extra = {}) {
      const data = new FormData();
      data.append('function', fn);
      Object.entries(extra).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') data.append(k, v);
      });
      return execute(data, authUrl);
    },
    formatMoney(v) {
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(Number(v) || 0);
    },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    flash(msg, isError = false) {
      this.message = msg;
      this.messageError = isError;
      setTimeout(() => {
        if (this.message === msg) this.message = '';
      }, 4000);
    },
    async loadAll() {
      if (!this.employee?.id || !this.payrollConfigId) return;
      this.loading = true;
      try {
        const [catRes, compRes, adjRes] = await Promise.all([
          this.post('getPayComponents'),
          this.post('getEmployeePayComponents', {
            employee_id: this.employee.id,
            payroll_config_id: this.payrollConfigId
          }),
          this.post('getSalaryAdjustments', {
            employeeId: this.employee.id,
            status: 'Pending',
            limit: 50
          })
        ]);
        if (catRes.data?.success) {
          this.catalog = catRes.data.data?.pay_components || [];
        }
        if (compRes.data?.success) {
          this.employeeComponents = compRes.data.data?.components || [];
        }
        if (adjRes.data?.success) {
          this.pendingAdjustments = adjRes.data.data?.adjustments || [];
        }
      } catch (e) {
        this.flash('Failed to load pay data', true);
      } finally {
        this.loading = false;
      }
    },
    async saveComponent() {
      if (!this.employee?.id || !this.payrollConfigId) return;
      this.saving = true;
      try {
        const res = await this.post('setEmployeePayComponent', {
          employee_id: this.employee.id,
          payroll_config_id: this.payrollConfigId,
          pay_component_id: this.componentForm.pay_component_id,
          amount: this.componentForm.amount,
          effective_from: this.componentForm.effective_from
        });
        if (res.data?.success) {
          this.flash(res.data.message || 'Component saved');
          this.componentForm.pay_component_id = '';
          this.componentForm.amount = '';
          await this.loadAll();
          this.$emit('saved');
        } else {
          this.flash(res.data?.message || 'Save failed', true);
        }
      } catch (e) {
        this.flash('Save failed', true);
      } finally {
        this.saving = false;
      }
    },
    async removeComponent(row) {
      if (!confirm(`Remove ${row.component_name}?`)) return;
      this.saving = true;
      try {
        const res = await this.post('removeEmployeePayComponent', {
          employee_id: this.employee.id,
          payroll_config_id: this.payrollConfigId,
          id: row.id,
          pay_component_id: row.pay_component_id
        });
        if (res.data?.success) {
          this.flash('Component removed');
          await this.loadAll();
          this.$emit('saved');
        } else {
          this.flash(res.data?.message || 'Remove failed', true);
        }
      } catch (e) {
        this.flash('Remove failed', true);
      } finally {
        this.saving = false;
      }
    },
    async saveAdjustment() {
      if (!this.employee?.id) return;
      this.saving = true;
      try {
        const res = await this.post('createSalaryAdjustment', {
          employee_id: this.employee.id,
          adjustment_type: this.adjustmentForm.adjustment_type,
          description: this.adjustmentForm.description,
          amount: this.adjustmentForm.amount,
          effective_date: this.adjustmentForm.effective_date,
          is_taxable: this.adjustmentForm.is_taxable ? 1 : 0,
          is_percentage: 0
        });
        if (res.data?.success) {
          this.flash(res.data.message || 'Adjustment added');
          this.adjustmentForm.description = '';
          this.adjustmentForm.amount = '';
          await this.loadAll();
          this.$emit('saved');
        } else {
          this.flash(res.data?.message || 'Failed', true);
        }
      } catch (e) {
        this.flash('Failed to add adjustment', true);
      } finally {
        this.saving = false;
      }
    },
    async cancelAdjustment(adj) {
      if (!confirm('Cancel this pending adjustment?')) return;
      this.saving = true;
      try {
        const res = await this.post('cancelSalaryAdjustment', {
          adjustment_id: adj.id,
          employee_id: this.employee.id
        });
        if (res.data?.success) {
          this.flash('Adjustment cancelled');
          await this.loadAll();
          this.$emit('saved');
        } else {
          this.flash(res.data?.message || 'Cancel failed', true);
        }
      } catch (e) {
        this.flash('Cancel failed', true);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.epc-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.epc-embedded {
  width: 100%;
}

.epc-embedded-inner {
  width: 100%;
}

.epc-modal {
  background: #fff;
  border-radius: 20px;
  width: min(760px, 100%);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.22);
}

.epc-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px 12px;
  border-bottom: 1px solid #eef2ff;
}

.epc-title {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.epc-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.epc-close {
  border: none;
  background: #f1f5f9;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 22px;
  transition: background 0.2s;
}

.epc-close:hover {
  background: #e2e8f0;
}

.epc-tabs {
  display: flex;
  gap: 8px;
  padding: 12px 24px 0;
}

.epc-tabs-embedded {
  padding: 0 0 16px;
}

.epc-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 10px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.epc-tab i {
  font-size: 16px;
}

.epc-tab:hover {
  border-color: #c7d2fe;
  background: #f8fafc;
}

.epc-tab.active {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.3);
}

.epc-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.25);
  color: inherit;
}

.epc-tab:not(.active) .epc-tab-count {
  background: #e2e8f0;
  color: #475569;
}

.epc-tab:not(.active) .epc-tab-count.warn {
  background: #fef3c7;
  color: #b45309;
}

.epc-body {
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
}

.epc-body-embedded {
  padding: 0;
}

.epc-hint {
  font-size: 12px;
  color: #475569;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  border: 1px solid #e0e7ff;
  border-radius: 12px;
  padding: 12px 14px;
  margin: 0 0 16px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.epc-form-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 20px;
}

.epc-form-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 14px;
}

.epc-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.epc-field label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
  margin-bottom: 6px;
}

.epc-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.epc-input:focus {
  outline: none;
  border-color: #818cf8;
  box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.2);
}

.epc-field-action {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.epc-check label {
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: none;
  font-size: 13px;
  margin-top: 24px;
}

.epc-btn {
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: transform 0.15s, box-shadow 0.15s;
}

.epc-btn.primary {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
}

.epc-btn.primary:hover:not(:disabled) {
  transform: translateY(-1px);
}

.epc-btn.primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.epc-btn.outline {
  background: #fff;
  border: 1px solid #cbd5e1;
  color: #475569;
}

.epc-item-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.epc-item-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.epc-item-card:hover {
  border-color: #c7d2fe;
  box-shadow: 0 2px 8px rgba(79, 70, 229, 0.08);
}

.epc-item-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.epc-item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.epc-item-info strong {
  font-size: 14px;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.epc-item-meta {
  font-size: 12px;
  color: #94a3b8;
}

.epc-item-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.epc-item-amount {
  font-size: 14px;
  font-weight: 700;
}

.epc-item-amount.earn {
  color: #059669;
}

.epc-item-amount.ded {
  color: #dc2626;
}

.epc-icon-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.epc-icon-btn.danger:hover {
  background: #fee2e2;
  color: #dc2626;
}

.epc-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.epc-pill.earn {
  background: #d1fae5;
  color: #065f46;
}

.epc-pill.ded {
  background: #fee2e2;
  color: #991b1b;
}

.epc-empty {
  text-align: center;
  color: #94a3b8;
  padding: 32px 16px;
  font-size: 13px;
}

.epc-empty i {
  display: block;
  font-size: 36px;
  margin-bottom: 8px;
  opacity: 0.5;
}

.epc-empty p {
  margin: 0;
}

.epc-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px;
  color: #64748b;
  font-size: 13px;
}

.epc-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: epc-spin 0.7s linear infinite;
}

@keyframes epc-spin {
  to { transform: rotate(360deg); }
}

.epc-message {
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  background: #d1fae5;
  color: #065f46;
}

.epc-message.error {
  background: #fee2e2;
  color: #991b1b;
}

.epc-footer {
  padding: 12px 24px 20px;
  border-top: 1px solid #eef2ff;
  display: flex;
  justify-content: flex-end;
}
</style>
